// Consoles/Plugins/publications.plugin.js
import { Chip, Button } from "@mui/material";
import supabase from "../../../../../utils/supabase";
import { selectInBatched } from "../../../../../utils/supabase-batch";

const isUuid = (s) =>
  typeof s === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

// 表示ユーティリティ
const statusChip = (value) => {
  if (!value) return "—";
  const map = { complete: "success", in_progress: "warning", not_yet: "default" };
  return <Chip size="small" color={map[value] ?? "default"} label={value} />;
};

const stripTags = (s) => (s || "").replace(/<[^>]*>/g, " ");
const fmtAuthor = (r) => {
  if (!r) return "";
  const ln = r.last_name || "";
  const fn = r.first_name || "";
  return `${ln}${ln && fn ? ", " : ""}${fn}`;
};

// .or に渡す文字列用：カンマをエスケープ
const escCommas = (s) => String(s || "").replace(/,/g, "\\,");

/*
  ポイント:
  - journal の埋め込みは「エイリアス:テーブル!FK制約名」
  - journals の列名は name_eng / name_org
  - publications_authors は既知の FK 制約名で明示（環境に合わせて変更可）
*/
const publicationsPlugin = {
  table: "publications",

  // ← コメントを文字列に含めないこと！
  select: `
    id,
    publication_date,
    title_english,
    title_original,
    journal_id,
    volume,
    number,
    article_id,
    page,
    doi,
    is_open_access,
    scientific_name_status,
    taxonomic_act_status,
    distribution_status,
    ecological_data_status,
    journal:journals!publications_journal_id_fkey ( id, name_eng, name_org ),
    publications_authors:publications_authors!fk_51a9de83637fd53c8b45124b (
      author_order,
      author:researcher_id ( id, last_name, first_name )
    )
  `,

  defaultSort: { field: "publication_date", sort: "desc" },

  async fetch({ page, pageSize, orderBy, ascending, search }) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const likeOr = () => {
      if (!search) return undefined;
      const like = `%${escCommas(search)}%`;
      return [
        `id.ilike.${like}`,
        `title_english.ilike.${like}`,
        `title_original.ilike.${like}`,
        `doi.ilike.${like}`,
      ].join(",");
    };

    // 1) 本体
    let query = supabase
      .from("publications")
      .select(
        `
          id,
          id_uuid,
          publication_type,
          publication_date,
          online_first_date,
          title_english,
          title_original,
          title_other,
          abstract_english,
          abstract_other,
          journal_id,
          volume,
          number,
          article_id,
          page,
          doi,
          is_open_access,
          total_pages,
          city,
          country,
          parent_book_id,
          parent_book_id_uuid,
          degree_name,
          remark,
          isbn_hardback,
          isbn_epub,
          isbn_pdf,
          scientific_name_status,
          taxonomic_act_status,
          distribution_status,
          ecological_data_status
        `,
        { count: "exact" }
      )
      .range(from, to);

    const orExpr = likeOr();
    if (orExpr) query = query.or(orExpr);

    // orderBy が無いカラムの場合 fallback
    try {
      query = query.order(orderBy, { ascending });
    } catch {
      query = query.order("publication_date", { ascending: false });
    }

    const { data, error, count } = await query;
    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];
    const ids = rows.map((r) => r.id).filter(Boolean);
    const idUuids = rows.map((r) => r.id_uuid).filter(isUuid);
    const journalIds = [...new Set(rows.map((r) => r.journal_id).filter(Boolean))];

    // 2) 著者リンク（実テーブル定義に合わせる）
    let links = [];
    // publications_authors: publication_id (text) と publication_id_uuid (uuid)
    if (ids.length) {
      try {
        const relById = await selectInBatched({
          table: "publications_authors",
          column: "publication_id",
          values: ids,
          select: "publication_id, publication_id_uuid, author_order, researcher_id",
          chunkSize: 50,
          order: { column: "author_order", ascending: true },
        });
        links = links.concat(relById || []);
      } catch (e) {
        console.warn("[publications.fetch] publications_authors by id failed", e?.message || e);
      }
    }
    if (idUuids.length) {
      try {
        const relByUuid = await selectInBatched({
          table: "publications_authors",
          column: "publication_id_uuid",
          values: idUuids,
          select: "publication_id, publication_id_uuid, author_order, researcher_id",
          chunkSize: 50,
          order: { column: "author_order", ascending: true },
        });
        links = links.concat(relByUuid || []);
      } catch (e) {
        console.warn("[publications.fetch] publications_authors by uuid failed", e?.message || e);
      }
    }
    // 重複行（id/uuid 両方に同じ紐付けがあるなど）を排除
    const dedupedLinks = Array.from(
      new Map(
        links.map((l) => {
          const pubKey = l.publication_id_uuid || l.publication_id || "";
          const authorKey = l.researcher_id || l.author_id || "";
          const orderKey = l.author_order ?? "";
          const key = `${pubKey}::${authorKey}::${orderKey}`;
          return [key, l];
        })
      ).values()
    );
    console.log("[publications.fetch] links collected", {
      total: links.length,
      deduped: dedupedLinks.length,
      sample: dedupedLinks.slice(0, 5),
    });

    const authorIds = []; // publication_authors は未使用
    const researcherIds = [...new Set(dedupedLinks.map((l) => l.researcher_id).filter(Boolean))];

    const authorNameMap = new Map();
    if (authorIds.length) {
      const { data: adata } = await supabase
        .from("authors")
        .select("id, last_name, first_name")
        .in("id", authorIds);
      (adata || []).forEach((a) => {
        authorNameMap.set(a.id, {
          last_name: a.last_name || "",
          first_name: a.first_name || "",
        });
      });
    }

    const researcherNameMap = new Map();
    if (researcherIds.length) {
      const { data: rdata } = await supabase
        .from("researchers")
        .select("id, last_name, first_name")
        .in("id", researcherIds);
      (rdata || []).forEach((r) => {
        researcherNameMap.set(r.id, {
          last_name: r.last_name || "",
          first_name: r.first_name || "",
        });
      });
    }
    console.log("[publications.fetch] nameMaps", {
      authors: authorNameMap.size,
      researchers: researcherNameMap.size,
    });

    const authorsMap = dedupedLinks.reduce((m, l) => {
      const key = l.publication_id_uuid || l.publication_id || "";
      const arr = m.get(key) || [];
      const nameObj =
        (l.researcher_id && researcherNameMap.get(l.researcher_id)) ||
        (l.author_id && authorNameMap.get(l.author_id)) ||
        {};
      arr.push({
        id: l.researcher_id || l.author_id || `${key}-${l.author_order}`,
        last_name: nameObj.last_name || "",
        first_name: nameObj.first_name || "",
        author_order: l.author_order ?? 0,
      });
      return m.set(key, arr);
    }, new Map());
    console.log("[publications.fetch] authorsMap size", authorsMap.size);

    // 3) ジャーナル名 (v_journal_map があれば優先、無ければ journals を直接)
    let journalMap = new Map();
    if (journalIds.length) {
      try {
        const jdata2 = await selectInBatched({
          table: "journals",
          column: "id",
          values: journalIds,
          select: "id, name_eng, name_org",
          chunkSize: 50,
        });
        (jdata2 || []).forEach((j) => {
          journalMap.set(String(j.id), j.name_eng || j.name_org || "");
        });
      } catch (e2) {
        console.error("[publications.fetch] journals fallback failed", e2?.message || e2);
      }
    }
    console.log("[publications.fetch] maps", { authorsMap: authorsMap.size, journalMap: journalMap.size });

    const mapped = rows.map((row) => {
      const authorsArr =
        (authorsMap.get(row.id) || authorsMap.get(row.id_uuid) || []).sort(
          (a, b) => a.author_order - b.author_order
        );
      const authorsText = authorsArr.map(fmtAuthor).filter(Boolean).join("; ");
      return publicationsPlugin.normalize({
        ...row,
        journal: { name_eng: journalMap.get(String(row.journal_id)) || "" },
        publications_authors: authorsArr.map((a) => ({
          author_order: a.author_order,
          author: { id: a.id, last_name: a.last_name, first_name: a.first_name },
        })),
      });
    });

    return { rows: mapped, total: typeof count === "number" ? count : mapped.length };
  },

  // Supabase JS の .or() に渡す条件（カンマ区切り）
  or(search) {
    if (!search) return undefined;
    const like = `%${escCommas(search)}%`;
    return [
      `id.ilike.${like}`,
      `title_english.ilike.${like}`,
      `title_original.ilike.${like}`,
      `doi.ilike.${like}`,
    ].join(",");
  },

  normalize(row) {
    const authors =
      (row.publications_authors || [])
        .sort((a, b) => (a?.author_order ?? 0) - (b?.author_order ?? 0))
        .map((x) => x.author)
        .filter(Boolean);

    const authorsText = (authors || []).map(fmtAuthor).filter(Boolean).join("; ");

    return {
      ...row,
      authors,
      authorsText,
      titleEnglishHtml: row.title_english || "",
      titleOriginalHtml: row.title_original || "",
      titleEnglishText: stripTags(row.title_english),
      titleOriginalText: stripTags(row.title_original),
      journalName: row.journal?.name_eng || row.journal?.name_org || "",
    };
  },

  // クライアント側の追加フィルタ（補完）
  searchLocal(rows, q) {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      (r.id || "").toLowerCase().includes(needle) ||
      (r.doi || "").toLowerCase().includes(needle) ||
      (r.titleEnglishText || "").toLowerCase().includes(needle) ||
      (r.titleOriginalText || "").toLowerCase().includes(needle) ||
      (r.journalName || "").toLowerCase().includes(needle) ||
      (r.authorsText || "").toLowerCase().includes(needle)
    );
  },

  columns({ onEdit }) {
    return [
      {
        field: "__actions",
        headerName: "Actions",
        width: 96,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button size="small" variant="outlined" onClick={() => onEdit?.(p.row)}>
            Edit
          </Button>
        ),
        headerClassName: "col-actions",
        cellClassName: "col-actions",
      },
      {
        field: "id",
        headerName: "ID",
        width: 220,
        renderCell: (p) => <span style={{ fontWeight: 600 }}>{p.row.id}</span>,
        headerClassName: "col-id",
        cellClassName: "col-id",
      },
      {
        field: "authors",
        headerName: "Authors",
        width: 260,
        sortable: false,
        valueGetter: (p) => p.row?.authorsText || "",
        renderCell: (p) =>
          (p.row?.authors || []).length ? (
            <ol style={{ margin: 0, paddingInlineStart: 18 }}>
              {p.row.authors.map((a, i) => (
                <li key={a?.id || i}>{fmtAuthor(a)}</li>
              ))}
            </ol>
          ) : "—",
      },
      {
        field: "title_english",
        headerName: "Title (English)",
        flex: 1.2,
        minWidth: 260,
        sortable: false,
        renderCell: (p) => (
          <span dangerouslySetInnerHTML={{ __html: p.row?.titleEnglishHtml || "" }} />
        ),
      },
      {
        field: "title_original",
        headerName: "Title (Original)",
        flex: 1,
        minWidth: 240,
        sortable: false,
        renderCell: (p) => (
          <span dangerouslySetInnerHTML={{ __html: p.row?.titleOriginalHtml || "" }} />
        ),
      },
      {
        field: "journal",
        headerName: "Journal",
        width: 200,
        valueGetter: (p) => p.row?.journal?.name_eng || p.row?.journal?.name_org || "",
        renderCell: (p) => <i>{p.row?.journal?.name_eng || p.row?.journal?.name_org || "—"}</i>,
      },
      {
        field: "doi",
        headerName: "DOI",
        width: 180,
        renderCell: (p) =>
          p.row?.doi ? (
            <a
              href={`https://doi.org/${p.row.doi}`}
              target="_blank"
              rel="noreferrer"
              style={{
                color: "#7dd3fc",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              {p.row.doi}
            </a>
          ) : "—",
      },
      { field: "volume", headerName: "Vol.", width: 70 },
      { field: "number", headerName: "No.", width: 70 },
      { field: "article_id", headerName: "Article ID", width: 100 },
      { field: "page", headerName: "Pages", width: 90 },
      {
        field: "publication_date",
        headerName: "Date",
        width: 110,
        valueGetter: (p) => (p.row?.publication_date || "").slice(0, 10),
      },
      {
        field: "is_open_access",
        headerName: "Open",
        width: 72,
        renderCell: (p) =>
          p.row?.is_open_access ? <Chip size="small" color="success" label="Open" /> : "—",
      },
      {
        field: "scientific_name_status",
        headerName: "Sci. Name?",
        width: 140,
        renderCell: (p) => statusChip(p.row?.scientific_name_status),
      },
      {
        field: "taxonomic_act_status",
        headerName: "Tax. Act?",
        width: 130,
        renderCell: (p) => statusChip(p.row?.taxonomic_act_status),
      },
      {
        field: "distribution_status",
        headerName: "Dist.?",
        width: 110,
        renderCell: (p) => statusChip(p.row?.distribution_status),
      },
      {
        field: "ecological_data_status",
        headerName: "Ecol.?",
        width: 110,
        renderCell: (p) => statusChip(p.row?.ecological_data_status),
      },
    ];
  },
};

export default publicationsPlugin;
export { publicationsPlugin };
